import { useState, useEffect, useRef } from "react";
import styles from "./ConfirmDeleteDialog.module.css";

/**
 * R-261 5d-5a — destructive-action confirm with an optional "request rescan"
 * checkbox + note. Shared by the classify panel (page/doc delete) and the
 * escalation context panel (delete + request rescan).
 *
 * A11y: aria-modal, Escape cancels, focus moves into dialog on open.
 */
export function ConfirmDeleteDialog({
  title,
  message,
  busy,
  onConfirm,
  onCancel,
  showRescanOption = true,
}: {
  title: string;
  message: string;
  busy: boolean;
  onConfirm: (rescan: { note?: string } | undefined) => void;
  onCancel: () => void;
  /** Document-specific "Request rescan" affordance. Defaults to true so every
   *  existing call site (classify panel, escalation context panel) keeps
   *  exact behavior. Callers whose delete target isn't a scanned document
   *  (e.g. automation deletes) should pass `false` — there is nothing to
   *  rescan. */
  showRescanOption?: boolean;
}) {
  const [requestRescan, setRequestRescan] = useState(false);
  const [note, setNote] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on mount so keyboard users are immediately
  // inside the modal — matches MisidentifiedTypeDialog's tabIndex={-1} pattern.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
      >
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.message}>{message}</p>
        {showRescanOption && (
          <label className={styles.rescanRow}>
            <input
              type="checkbox"
              checked={requestRescan}
              onChange={(e) => setRequestRescan(e.target.checked)}
              aria-label="Request rescan"
            />
            Request rescan
          </label>
        )}
        {showRescanOption && requestRescan && (
          <textarea
            className={styles.note}
            placeholder="Why a rescan is needed (optional)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.deleteBtn}
            disabled={busy}
            onClick={() =>
              onConfirm(requestRescan ? { note: note.trim() || undefined } : undefined)
            }
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
